import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

interface PatientFileFormData {
  file_number: string;
  last_name: string;
  first_name: string;
  birth_date: string;
  email: string;
  phone_number: string;
  gender: string;
  marital_status: string;
  height: number;
  weight: number;
  blood_type: string;
  allergies: string;
  chronic_conditions: string;
  current_medications: string;
  surgical_history: string;
  family_history: string;
  notes: string;
}

interface AddPatientFileDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

export const AddPatientFileDialog = ({ open, onOpenChange, onSuccess }: AddPatientFileDialogProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState<PatientFileFormData>({
    file_number: `PF-${Date.now().toString().slice(-6)}`,
    last_name: "",
    first_name: "",
    birth_date: "",
    email: "",
    phone_number: "",
    gender: "",
    marital_status: "",
    height: 0,
    weight: 0,
    blood_type: "",
    allergies: "",
    chronic_conditions: "",
    current_medications: "",
    surgical_history: "",
    family_history: "",
    notes: ""
  });

  const handleChange = (field: keyof PatientFileFormData, value: string | number) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      // Get current authenticated user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      // Create patient file data object
      const patientFileData = {
        professional_id: user.id,
        file_number: formData.file_number,
        last_name: formData.last_name,
        first_name: formData.first_name,
        birth_date: formData.birth_date || null,
        email: formData.email,
        phone_number: formData.phone_number,
        gender: formData.gender,
        marital_status: formData.marital_status,
        height: formData.height,
        weight: formData.weight,
        blood_type: formData.blood_type,
        allergies: formData.allergies,
        chronic_conditions: formData.chronic_conditions,
        current_medications: formData.current_medications,
        surgical_history: formData.surgical_history,
        family_history: formData.family_history,
        notes: formData.notes
      };

      // Insert into database
      const { error } = await supabase
        .from("patient_files")
        .insert(patientFileData);

      if (error) throw error;

      toast.success("Dossier patient créé avec succès");
      onOpenChange(false);
      onSuccess();
    } catch (error: any) {
      console.error("Error creating patient file:", error);
      toast.error(`Erreur lors de la création du dossier: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Nouveau dossier patient</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="grid gap-4 py-4">
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="file_number" className="text-right">
              Numéro de dossier
            </Label>
            <Input
              type="text"
              id="file_number"
              value={formData.file_number}
              onChange={(e) => handleChange("file_number", e.target.value)}
              className="col-span-3"
              disabled
            />
          </div>
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="last_name" className="text-right">
              Nom
            </Label>
            <Input
              type="text"
              id="last_name"
              value={formData.last_name}
              onChange={(e) => handleChange("last_name", e.target.value)}
              className="col-span-3"
              required
            />
          </div>
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="first_name" className="text-right">
              Prénom
            </Label>
            <Input
              type="text"
              id="first_name"
              value={formData.first_name}
              onChange={(e) => handleChange("first_name", e.target.value)}
              className="col-span-3"
              required
            />
          </div>
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="birth_date" className="text-right">
              Date de naissance
            </Label>
            <Input
              type="date"
              id="birth_date"
              value={formData.birth_date}
              onChange={(e) => handleChange("birth_date", e.target.value)}
              className="col-span-3"
            />
          </div>
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="email" className="text-right">
              Email
            </Label>
            <Input
              type="email"
              id="email"
              value={formData.email}
              onChange={(e) => handleChange("email", e.target.value)}
              className="col-span-3"
            />
          </div>
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="phone_number" className="text-right">
              Téléphone
            </Label>
            <Input
              type="tel"
              id="phone_number"
              value={formData.phone_number}
              onChange={(e) => handleChange("phone_number", e.target.value)}
              className="col-span-3"
            />
          </div>
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="gender" className="text-right">
              Genre
            </Label>
            <Select onValueChange={(value) => handleChange("gender", value)}>
              <SelectTrigger className="col-span-3">
                <SelectValue placeholder="Sélectionner" defaultValue={formData.gender} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="male">Homme</SelectItem>
                <SelectItem value="female">Femme</SelectItem>
                <SelectItem value="other">Autre</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="marital_status" className="text-right">
              Situation matrimoniale
            </Label>
            <Select onValueChange={(value) => handleChange("marital_status", value)}>
              <SelectTrigger className="col-span-3">
                <SelectValue placeholder="Sélectionner" defaultValue={formData.marital_status} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="single">Célibataire</SelectItem>
                <SelectItem value="married">Marié(e)</SelectItem>
                <SelectItem value="divorced">Divorcé(e)</SelectItem>
                <SelectItem value="widowed">Veuf(ve)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="height" className="text-right">
              Taille (cm)
            </Label>
            <Input
              type="number"
              id="height"
              value={formData.height}
              onChange={(e) => handleChange("height", Number(e.target.value))}
              className="col-span-3"
            />
          </div>
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="weight" className="text-right">
              Poids (kg)
            </Label>
            <Input
              type="number"
              id="weight"
              value={formData.weight}
              onChange={(e) => handleChange("weight", Number(e.target.value))}
              className="col-span-3"
            />
          </div>
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="blood_type" className="text-right">
              Groupe sanguin
            </Label>
            <Select onValueChange={(value) => handleChange("blood_type", value)}>
              <SelectTrigger className="col-span-3">
                <SelectValue placeholder="Sélectionner" defaultValue={formData.blood_type} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="A+">A+</SelectItem>
                <SelectItem value="A-">A-</SelectItem>
                <SelectItem value="B+">B+</SelectItem>
                <SelectItem value="B-">B-</SelectItem>
                <SelectItem value="AB+">AB+</SelectItem>
                <SelectItem value="AB-">AB-</SelectItem>
                <SelectItem value="O+">O+</SelectItem>
                <SelectItem value="O-">O-</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="allergies" className="text-right">
              Allergies
            </Label>
            <Textarea
              id="allergies"
              value={formData.allergies}
              onChange={(e) => handleChange("allergies", e.target.value)}
              className="col-span-3"
            />
          </div>
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="chronic_conditions" className="text-right">
              Conditions chroniques
            </Label>
            <Textarea
              id="chronic_conditions"
              value={formData.chronic_conditions}
              onChange={(e) => handleChange("chronic_conditions", e.target.value)}
              className="col-span-3"
            />
          </div>
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="current_medications" className="text-right">
              Médications actuelles
            </Label>
            <Textarea
              id="current_medications"
              value={formData.current_medications}
              onChange={(e) => handleChange("current_medications", e.target.value)}
              className="col-span-3"
            />
          </div>
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="surgical_history" className="text-right">
              Antécédents chirurgicaux
            </Label>
            <Textarea
              id="surgical_history"
              value={formData.surgical_history}
              onChange={(e) => handleChange("surgical_history", e.target.value)}
              className="col-span-3"
            />
          </div>
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="family_history" className="text-right">
              Antécédents familiaux
            </Label>
            <Textarea
              id="family_history"
              value={formData.family_history}
              onChange={(e) => handleChange("family_history", e.target.value)}
              className="col-span-3"
            />
          </div>
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="notes" className="text-right">
              Notes
            </Label>
            <Textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => handleChange("notes", e.target.value)}
              className="col-span-3"
            />
          </div>
          <Button type="submit" disabled={isLoading}>
            {isLoading ? (
              <>
                Création en cours...
                <Loader2 className="ml-2 h-4 w-4 animate-spin" />
              </>
            ) : (
              "Créer le dossier patient"
            )}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
